import { useMemo, useState } from "react";
import { brl } from "@/lib/api";
import { EmptyState, Surface } from "@/components/ds";
import { BarChart3 } from "lucide-react";
import { BarsCompare, C, CategoryDonut } from "./charts";

const SORTS = [
  ["revenue", "Faturamento"],
  ["quantity", "Quantidade"],
  ["profit", "Lucro"],
  ["margin", "Margem"],
];

export function FinanceAnalysis({ data }) {
  const [sort, setSort] = useState("revenue");

  const products = useMemo(
    () =>
      [...data.top_products].sort((a, b) => (Number(b[sort]) || 0) - (Number(a[sort]) || 0)),
    [data.top_products, sort]
  );

  return (
    <div className="space-y-6" data-testid="finance-analysis">
      <Surface className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-medium md:text-lg">Top produtos vendidos</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Custo vem da calculadora de custos do produto. Sem custo cadastrado, mostramos
              "não disponível" em vez de estimar.
            </p>
          </div>
          <div className="flex flex-wrap gap-1">
            {SORTS.map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSort(key)}
                data-testid={`sort-${key}`}
                className={`rounded-full border px-3 py-1 text-[11px] transition-colors ${
                  sort === key
                    ? "border-brand/50 bg-brand/15 text-brand"
                    : "border-white/10 text-muted-foreground hover:text-foreground"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {products.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title="Nenhuma venda no período"
            description="Escolha outro período para ver o desempenho dos produtos."
          />
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="pb-2 pr-3">Produto</th>
                  <th className="pb-2 pr-3 text-right">Qtd</th>
                  <th className="pb-2 pr-3 text-right">Faturamento</th>
                  <th className="pb-2 pr-3 text-right">Custo</th>
                  <th className="pb-2 pr-3 text-right">Lucro</th>
                  <th className="pb-2 text-right">Margem</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr
                    key={p.key}
                    className="border-b border-white/5"
                    data-testid={`top-product-${p.key}`}
                  >
                    <td className="py-2.5 pr-3">
                      <span className="font-medium">{p.name}</span>
                      {p.variation_name && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          · {p.variation_name}
                        </span>
                      )}
                      {p.kind === "KIT" && (
                        <span className="ml-2 rounded-full border border-primary/40 px-1.5 text-[10px] uppercase text-brand">
                          kit
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-num">{p.quantity}</td>
                    <td className="py-2.5 pr-3 text-right font-num">{brl(p.revenue)}</td>
                    <td className="py-2.5 pr-3 text-right font-num text-muted-foreground">
                      {p.cost === null ? "não disponível" : brl(p.cost)}
                    </td>
                    <td
                      className={`py-2.5 pr-3 text-right font-num ${
                        p.profit === null
                          ? "text-muted-foreground"
                          : p.profit >= 0
                          ? "text-emerald-300"
                          : "text-red-300"
                      }`}
                    >
                      {p.profit === null ? "—" : brl(p.profit)}
                    </td>
                    <td className="py-2.5 text-right font-num">
                      {p.margin === null ? "—" : `${p.margin}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Surface>

      <div className="grid gap-4 xl:grid-cols-2">
        <Surface className="p-5" data-testid="by-origin-card">
          <h3 className="text-sm font-medium">Vendas por origem</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            De onde vem o faturamento no período
          </p>
          {data.by_origin.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem vendas no período.</p>
          ) : (
            <CategoryDonut
              data={data.by_origin.map((o) => ({ name: o.label, value: o.total }))}
              testid="chart-origin"
            />
          )}
        </Surface>

        <Surface className="p-5" data-testid="expenses-category-card">
          <h3 className="text-sm font-medium">Despesas por categoria</h3>
          <p className="mb-3 text-xs text-muted-foreground">
            {data.expenses_count} lançamento(s) no período
          </p>
          {data.expenses_by_category.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem despesas no período.</p>
          ) : (
            <CategoryDonut
              data={data.expenses_by_category.map((c) => ({
                name: c.category,
                value: c.total,
              }))}
              testid="chart-expenses-category"
            />
          )}
        </Surface>
      </div>

      <Surface className="p-5">
        <h3 className="text-sm font-medium">Produção × Vendas por mês</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          Vendido, custo real das produções concluídas e despesas pagas
        </p>
        <BarsCompare
          data={data.series}
          bars={[
            { key: "sold", name: "Vendido", color: C.sold },
            { key: "production_cost", name: "Custo de produção", color: C.cost },
            { key: "expenses", name: "Despesas", color: C.expense },
          ]}
          height={260}
          testid="chart-production-sales"
        />
      </Surface>
    </div>
  );
}
